/**
 * @file consent-texts-diff.ts
 * @description «Отличия от ред. N» для текстов согласия платформы и какие
 * виды баннера затрагивает правка (phase19.16, партия Р3г, v0.19.62;
 * db-design-r3g-texts §6; uiux-r3g-consent-texts.md §3, §4 и решение §8 п. 4
 * — «условия согласия меняются только при правке полей, видимых в данном
 * виде баннера»).
 *
 * Зачем в shared: экран платформы показывает подсвеченные поля и окно
 * публикации («посетители магазинов без счётчиков увидят уведомление
 * снова» / «окно о риске видит только владелец»), а API может сравнить
 * видимые тексты двух редакций при пересчёте условий согласия. Видимость
 * поля задаёт реестр `CONSENT_TEXT_FIELDS` (consent-texts-check.ts).
 */

import { CONSENT_TEXT_FIELDS, consentTextFields, type ConsentTextFieldInfo } from "./consent-texts-check.js";
import type { ConsentBannerTexts, ConsentNoticeRiskTexts, SiteConsentBannerKind } from "./consent-texts.js";
import { consentFingerprint, enabledCounterServices, type SiteCounters } from "../site-theme/counters.js";
import { canonicalJson } from "../site-theme/snapshot.js";

/** Изменённое поле редакции. */
export interface ConsentTextsDiffItem {
  field: string;
  label: string;
  before: string | null;
  after: string | null;
}

/** Виды баннера в порядке показа. */
const BANNER_KINDS: readonly SiteConsentBannerKind[] = ["consent", "notice", "info"];

function valueAt(root: unknown, path: string): string | null {
  let node: unknown = root;
  for (const key of path.split(".")) {
    if (typeof node !== "object" || node === null) return null;
    node = (node as Record<string, unknown>)[key];
  }
  return typeof node === "string" ? node : null;
}

/**
 * Поля, которые различаются, в порядке формы. Сравниваются поля реестра
 * обоих видов: у редакций одного вида чужие поля просто отсутствуют
 * (null === null). Редакция без поля (старая схема) — `null` у этой стороны.
 */
export function diffConsentTexts(
  before: ConsentBannerTexts | ConsentNoticeRiskTexts | null,
  after: ConsentBannerTexts | ConsentNoticeRiskTexts,
): ConsentTextsDiffItem[] {
  const items: ConsentTextsDiffItem[] = [];
  for (const info of CONSENT_TEXT_FIELDS) {
    const [b, a] = [valueAt(before, info.path), valueAt(after, info.path)];
    if (b !== a) items.push({ field: info.path, label: info.label, before: b, after: a });
  }
  return items;
}

const FIELD_BY_PATH: ReadonlyMap<string, ConsentTextFieldInfo> = new Map(CONSENT_TEXT_FIELDS.map((f) => [f.path, f]));

/**
 * Какие виды баннера затронуты правкой (решение §8 п. 4): поле влияет на
 * вид, только если посетитель видит его в этом виде. Пусто — посетителей
 * правка не касается (например, правилось только окно о риске).
 * `introDefault` считается видимым у `consent` и `notice`, хотя у магазина
 * со своим вступлением его не видно, — осторожнее переспросить.
 */
export function consentTextsAffect(diff: readonly Pick<ConsentTextsDiffItem, "field">[]): SiteConsentBannerKind[] {
  const touched = new Set<SiteConsentBannerKind>();
  for (const item of diff) {
    for (const kind of FIELD_BY_PATH.get(item.field)?.visibleIn ?? []) touched.add(kind);
  }
  return BANNER_KINDS.filter((kind) => touched.has(kind));
}

/** Поля баннера, которые посетитель видит в этом виде (или хотя бы в одном из видов списка). */
export function consentTextsVisibleFields(kind: SiteConsentBannerKind | readonly SiteConsentBannerKind[]): string[] {
  const kinds: readonly SiteConsentBannerKind[] = typeof kind === "string" ? [kind] : kind;
  return consentTextFields("BANNER")
    .filter((f) => f.visibleIn.some((k) => kinds.includes(k)))
    .map((f) => f.path);
}

/**
 * Канонический JSON видимых в этом виде полей — для сравнения условий
 * согласия по смыслу, а не по номеру редакции: две редакции, различающиеся
 * только невидимыми в виде полями, дают одну строку
 * (`consentFingerprint(counters, { texts })`).
 */
export function consentTextsVisibleJson(
  texts: ConsentBannerTexts,
  kind: SiteConsentBannerKind | readonly SiteConsentBannerKind[],
): string {
  return canonicalJson(Object.fromEntries(consentTextsVisibleFields(kind).map((path) => [path, valueAt(texts, path)])));
}

/**
 * Виды баннера, тексты которых может увидеть посетитель при этих настройках;
 * пусто — баннера нет. Режим «Уведомление» — объединение `notice` и
 * `consent`: запрет платформы (`SITE_CONSENT_NOTICE_ALLOWED`) показывает
 * фразы «после согласия» без новой публикации магазина.
 */
function visibleKinds(counters: SiteCounters): SiteConsentBannerKind[] {
  if (enabledCounterServices(counters).length === 0) return counters.noticeWithoutCounters ? ["info"] : [];
  return counters.mode === "notice" ? ["consent", "notice"] : ["consent"];
}

/**
 * Отпечаток условий согласия по смыслу текста (решение §8 п. 4): вместо
 * номера редакции — видимые в данном виде поля переданной редакции. Переход
 * магазина на редакцию, где правились только невидимые ему поля, условий не
 * меняет. Сравнивать можно только отпечатки, посчитанные одним способом
 * (обе стороны — с текстами).
 */
export function consentFingerprintWithTexts(counters: SiteCounters, texts: ConsentBannerTexts): string {
  const kinds = visibleKinds(counters);
  return consentFingerprint(counters, { textsKey: kinds.length === 0 ? "" : consentTextsVisibleJson(texts, kinds) });
}
